import ManagedErrorToasts from '@/components/error/ManagedErrorToasts';
import type { SubmitTokenWithAgreementData } from '@/components/escript/token/types';
import { ESCRIPT_INVITE_EXPIRED_ROUTE, ESCRIPT_ONBOARDING_ROUTE } from '@/routes/route-paths';
import { useErrorManagement } from '@montugroup/circuit-shared/context/ErrorManagement';
import type { AlertColor } from '@mui/material';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

export type PreOnboardingStates =
  | 'SUBMIT_TOKEN'
  | 'SCRIPT_VALID'
  | 'SCRIPT_AWAITING_REVIEW'
  | 'SCRIPT_VALID_OUT_OF_STOCK'
  | 'DUPLICATE_TOKEN_PENDING_INVITE'
  | 'ACCOUNT_ALREADY_EXISTS'
  | 'INVALID_TOKEN_PENDING_INVITE'
  | 'ERX_FALLBACK_STRATEGY';

export interface PreOnboardingCustomer {
  email?: string;
  phone?: string;
  preOnboardUserId: number;
}

export interface AlertState {
  show: boolean;
  severity?: AlertColor;
  title?: string;
  content: string;
}

export interface PreOnboardingContextType {
  preOnboardingState: PreOnboardingStates;
  setPreOnboardingState: React.Dispatch<React.SetStateAction<PreOnboardingStates>>;
  customerDetails: null | PreOnboardingCustomer;
  setCustomerDetails: React.Dispatch<React.SetStateAction<null | PreOnboardingCustomer>>;
  alert: AlertState;
  setAlert: React.Dispatch<React.SetStateAction<AlertState>>;
  hideAlert: () => void;
  tokenInputRef: React.RefObject<HTMLDivElement | null>;
  resetOnboardingState: () => void;
}

export const PreOnboardingContext = createContext<PreOnboardingContextType | undefined>(undefined);

export const PreOnboardingStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preOnboardingState, setPreOnboardingState] = useState<PreOnboardingStates>('SUBMIT_TOKEN');
  const [customerDetails, setCustomerDetails] = useState<null | PreOnboardingCustomer>(null);
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    severity: 'success',
    title: '',
    content: '',
  });
  const tokenInputRef = useRef<HTMLDivElement | null>(null);

  const hideAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueError } = useErrorManagement();
  const form = useForm<SubmitTokenWithAgreementData>({
    defaultValues: {
      tokenInput: '',
      agreementCheckbox: false,
    },
  });
  const { reset, setFocus } = form;

  // Shows an error passed through navigation state on mount. Only the login page sends one, for an expired invite
  // link while enable_escript_expired_invite_recovery is off. Remove, with <ManagedErrorToasts />, along with that flag.
  useEffect(() => {
    if (location.state?.error) {
      enqueueError(location.state.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToMobileInput = useCallback(() => {
    const mobileInputContainer = tokenInputRef.current;

    if (mobileInputContainer) {
      mobileInputContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });

      setFocus('tokenInput');
    }
  }, [setFocus]);

  const resetOnboardingState = useCallback(() => {
    if (matchPath(ESCRIPT_INVITE_EXPIRED_ROUTE, location.pathname)) {
      navigate(ESCRIPT_ONBOARDING_ROUTE, { state: { focusTokenInput: true } });
      return;
    }
    if (preOnboardingState !== 'SUBMIT_TOKEN') {
      setPreOnboardingState('SUBMIT_TOKEN');
      reset();
      scrollToMobileInput();
    }
  }, [location.pathname, navigate, preOnboardingState, reset, setPreOnboardingState, scrollToMobileInput]);

  useEffect(() => {
    if (matchPath(ESCRIPT_ONBOARDING_ROUTE, location.pathname) && location.state?.focusTokenInput) {
      scrollToMobileInput();
      const state = { ...location.state };
      delete state.focusTokenInput;
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: Object.keys(state).length > 0 ? state : null },
      );
    }
  }, [location.pathname, location.search, location.hash, location.state, navigate, scrollToMobileInput]);

  const value = useMemo(() => {
    return {
      preOnboardingState,
      setPreOnboardingState,
      customerDetails,
      setCustomerDetails,
      alert,
      setAlert,
      hideAlert,
      tokenInputRef,
      resetOnboardingState,
    };
  }, [preOnboardingState, customerDetails, alert, resetOnboardingState]);

  return (
    <PreOnboardingContext.Provider value={value}>
      <ManagedErrorToasts />
      <FormProvider {...form}>{children}</FormProvider>
    </PreOnboardingContext.Provider>
  );
};
